import React from "react";
import { useDispatch, useSelector } from "react-redux";
import PropTypes from "prop-types";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  Typography,
  Snackbar,
  Alert,
} from "@mui/material";
import {
  deleteCustomer,
  updateCustomerActive,
  clearCustomerMessages,
} from "../../../store/customer/customerSlice";
import {
  AH_ACCENT,
  AH_BORDER,
  AH_MUTED,
  AH_TEXT,
  AH_WHITE,
  auditHubPageSx,
  outlinedButtonSx,
  primaryButtonSx,
} from "../../Compliance/AuditHub/theme";
import { getCustomerDisplayName, isCustomerActive } from "../utils/customerHelpers";

const DELETE_STEP = {
  DEACTIVATE: "deactivate",
  DELETE: "delete",
};

const footerCancelSx = {
  ...outlinedButtonSx,
  height: 36,
  minHeight: 36,
  backgroundColor: "#74788d",
  color: AH_WHITE,
  borderColor: "#74788d",
  "&:hover": { backgroundColor: "#636678", borderColor: "#636678", boxShadow: "none" },
  "&.Mui-disabled": { backgroundColor: "#74788d", color: AH_WHITE, opacity: 0.65 },
};

const DeleteCustomerDialog = ({ open, onClose, customer, onSuccess, onDeactivateSuccess }) => {
  const dispatch = useDispatch();
  const {
    isDeletingCustomer,
    deleteCustomerSuccess,
    deleteCustomerError,
    isUpdatingCustomerActive,
    updateCustomerActiveSuccess,
    updateCustomerActiveError,
  } = useSelector((state) => state.customer);

  const [step, setStep] = React.useState(DELETE_STEP.DELETE);
  const [localCustomer, setLocalCustomer] = React.useState(customer);
  const [snackbarOpen, setSnackbarOpen] = React.useState(false);
  const [snackbarMessage, setSnackbarMessage] = React.useState("");
  const [snackbarSeverity, setSnackbarSeverity] = React.useState("info");

  const customerName = getCustomerDisplayName(localCustomer || customer);
  const busy = isDeletingCustomer || isUpdatingCustomerActive;
  const isDeactivateStep = step === DELETE_STEP.DEACTIVATE;

  React.useEffect(() => {
    if (open) {
      setLocalCustomer(customer);
      setStep(isCustomerActive(customer) ? DELETE_STEP.DEACTIVATE : DELETE_STEP.DELETE);
      dispatch(clearCustomerMessages());
    }
  }, [open, customer, dispatch]);

  const handleClose = () => {
    if (!busy) onClose();
  };

  const handleDeactivateAndContinue = () => {
    const orgcode = localCustomer?.orgcode || customer?.orgcode;
    if (orgcode) {
      dispatch(updateCustomerActive({ orgcode, active: false }));
    }
  };

  const handlePermanentDelete = () => {
    const orgcode = localCustomer?.orgcode || customer?.orgcode;
    if (orgcode) {
      dispatch(deleteCustomer(orgcode));
    }
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
    setSnackbarMessage("");
    setSnackbarSeverity("info");
    dispatch(clearCustomerMessages());
  };

  React.useEffect(() => {
    if (!open || !updateCustomerActiveSuccess) return;

    if (step === DELETE_STEP.DEACTIVATE) {
      setLocalCustomer((prev) => ({
        ...(prev || customer),
        is_active: false,
        isActive: false,
        active: false,
      }));
      setStep(DELETE_STEP.DELETE);
      dispatch(clearCustomerMessages());
      onDeactivateSuccess?.();
    }
  }, [
    updateCustomerActiveSuccess,
    open,
    step,
    customer,
    dispatch,
    onDeactivateSuccess,
  ]);

  React.useEffect(() => {
    if (!open || !updateCustomerActiveError) return;

    if (step === DELETE_STEP.DEACTIVATE) {
      const errorMessage =
        typeof updateCustomerActiveError === "string"
          ? updateCustomerActiveError
          : updateCustomerActiveError.data ||
            updateCustomerActiveError.message ||
            "Failed to deactivate customer.";
      setSnackbarMessage(errorMessage);
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  }, [updateCustomerActiveError, open, step]);

  React.useEffect(() => {
    if (deleteCustomerSuccess) {
      const message =
        deleteCustomerSuccess.data ||
        deleteCustomerSuccess.message ||
        "Customer deleted successfully.";
      setSnackbarMessage(message);
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
      onSuccess();
      onClose();
      dispatch(clearCustomerMessages());
    }
  }, [deleteCustomerSuccess, onClose, onSuccess, dispatch]);

  React.useEffect(() => {
    if (deleteCustomerError) {
      const errorMessage =
        typeof deleteCustomerError === "string"
          ? deleteCustomerError
          : deleteCustomerError.data ||
            deleteCustomerError.message ||
            JSON.stringify(deleteCustomerError);
      setSnackbarMessage(errorMessage);
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  }, [deleteCustomerError]);

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth
        maxWidth="sm"
        aria-labelledby="delete-customer-dialog-title"
        aria-describedby="delete-customer-dialog-description"
        PaperProps={{
          sx: {
            ...auditHubPageSx,
            borderRadius: "12px",
            overflow: "hidden",
            maxWidth: 520,
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            minHeight: 56,
            px: 3,
            backgroundColor: AH_ACCENT,
          }}
        >
          <Typography
            id="delete-customer-dialog-title"
            sx={{
              fontWeight: 700,
              fontSize: "1.0625rem",
              color: AH_WHITE,
              lineHeight: 1.3,
              minWidth: 0,
            }}
          >
            {isDeactivateStep ? "Deactivate Before Delete" : "Delete Permanently"}
          </Typography>
          <Box
            component="button"
            type="button"
            aria-label="Close"
            disabled={busy}
            onClick={handleClose}
            sx={{
              p: 0,
              m: 0,
              border: "none",
              background: "transparent",
              color: AH_WHITE,
              fontSize: 28,
              fontWeight: 700,
              lineHeight: 1,
              cursor: busy ? "default" : "pointer",
              flexShrink: 0,
              opacity: busy ? 0.65 : 1,
            }}
          >
            ×
          </Box>
        </Box>

        <DialogContent
          id="delete-customer-dialog-description"
          sx={{ px: 3, py: 2.5, backgroundColor: AH_WHITE }}
        >
          {isDeactivateStep ? (
            <>
              <Typography
                sx={{
                  fontSize: "0.875rem",
                  color: AH_TEXT,
                  lineHeight: 1.6,
                  overflowWrap: "anywhere",
                }}
              >
                <Box component="span" sx={{ fontWeight: 600 }}>
                  {customerName}
                </Box>{" "}
                is currently active and must be deactivated before it can be deleted
                permanently.
              </Typography>
              <Typography
                sx={{ mt: 1.25, fontSize: "0.8125rem", color: AH_MUTED, lineHeight: 1.6 }}
              >
                Deactivating will suspend Billing and Invoice Generation. You can
                reactivate the customer at any time from this page.
              </Typography>
            </>
          ) : (
            <>
              <Typography
                sx={{
                  fontSize: "0.875rem",
                  color: AH_TEXT,
                  lineHeight: 1.6,
                  overflowWrap: "anywhere",
                }}
              >
                You are about to permanently delete{" "}
                <Box component="span" sx={{ fontWeight: 600 }}>
                  {customerName}
                </Box>
                .
              </Typography>
              <Typography
                sx={{ mt: 1.25, fontSize: "0.8125rem", color: AH_MUTED, lineHeight: 1.6 }}
              >
                This action cannot be undone and all associated customer data will be
                removed.
              </Typography>
            </>
          )}
        </DialogContent>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 1.5,
            px: 3,
            py: 2,
            borderTop: `1px solid ${AH_BORDER}`,
            backgroundColor: "#f8fafc",
          }}
        >
          <Button
            variant="contained"
            onClick={handleClose}
            disabled={busy}
            sx={footerCancelSx}
          >
            Cancel
          </Button>
          {isDeactivateStep ? (
            <Button
              variant="contained"
              onClick={handleDeactivateAndContinue}
              disabled={isUpdatingCustomerActive}
              startIcon={
                isUpdatingCustomerActive ? (
                  <CircularProgress size={14} color="inherit" />
                ) : null
              }
              sx={{ ...primaryButtonSx, height: 36, minHeight: 36, minWidth: 140 }}
            >
              {isUpdatingCustomerActive ? "Deactivating…" : "Yes, Deactivate"}
            </Button>
          ) : (
            <Button
              variant="contained"
              onClick={handlePermanentDelete}
              disabled={isDeletingCustomer}
              startIcon={
                isDeletingCustomer ? (
                  <CircularProgress size={14} color="inherit" />
                ) : null
              }
              sx={{
                ...primaryButtonSx,
                height: 36,
                minHeight: 36,
                minWidth: 160,
                backgroundColor: "#dc2626",
                "&:hover": { backgroundColor: "#b91c1c", boxShadow: "none" },
                "&.Mui-disabled": {
                  backgroundColor: "#fecaca",
                  color: "#fff",
                },
              }}
            >
              {isDeletingCustomer ? "Deleting…" : "Delete Permanently"}
            </Button>
          )}
        </Box>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

DeleteCustomerDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  customer: PropTypes.object,
  onSuccess: PropTypes.func.isRequired,
  onDeactivateSuccess: PropTypes.func,
};

export default DeleteCustomerDialog;
